import { ArrowRight, ArrowUpRight, BookOpen, Mail } from "lucide-react";
import type { MouseEventHandler } from "react";
import { courseLanguages, coursesAccess } from "../content/courses";
import "./courses-page.css";

export default function CoursesPage({ onEnquire }: {
  onEnquire: MouseEventHandler<HTMLAnchorElement>;
}) {
  return <div className="courses-page">
    <section className="courses-hero container" aria-labelledby="courses-title">
      <div>
        <p className="eyebrow"><span className="eyebrow-line" /> Courses at the Institute</p>
        <h1 id="courses-title">A path for<br /><em>thoughtful study.</em></h1>
        <p className="courses-lead">Explore India’s Vedic and śāstric traditions through guided study, reflection and practice.</p>
        <p className="courses-intro">Our first course drafts are being prepared with an invited group. Discover the approach below; invited learners sign in to view the drafts assigned to them.</p>
        <a className="text-link" href="#course-overview">Explore the courses <ArrowRight size={17} aria-hidden="true" /></a>
      </div>
      <aside className="courses-access" aria-labelledby="courses-access-title">
        <span className="courses-access-icon" aria-hidden="true"><Mail size={25} strokeWidth={1.4} /></span>
        <p className="eyebrow">Access by invitation</p>
        <h2 id="courses-access-title">Already invited?</h2>
        <p>Sign in with your invited email address to see the course drafts available to you.</p>
        {coursesAccess.signInUrl
          ? <a className="button primary courses-signin" href={coursesAccess.signInUrl}>Sign in with email <ArrowUpRight size={18} aria-hidden="true" /></a>
          : <p className="courses-access-pending" role="status">Email sign-in is being prepared.</p>}
        <small>Enrollment is arranged by the Institute. Access is limited to invited learners and their assigned courses.</small>
      </aside>
    </section>

    <section className="courses-overview container" id="course-overview" aria-labelledby="course-overview-title">
      <div className="courses-section-heading">
        <p className="eyebrow">Guided learning</p>
        <h2 id="course-overview-title">Begin, deepen, <em>explore.</em></h2>
      </div>
      <div className="courses-offerings">
        <article className="courses-offering">
          <div className="courses-offering-heading"><BookOpen size={22} strokeWidth={1.4} aria-hidden="true" /><span>Three levels · About one hour per level</span></div>
          <h3>Vedic Way of Life</h3>
          <p>A progressive course pathway, from an introduction to more sustained study.</p>
          <ol className="courses-levels">
            <li><span aria-hidden="true">01</span><div><h4>Beginner</h4><p>Start with an introduction.</p></div></li>
            <li><span aria-hidden="true">02</span><div><h4>Intermediate</h4><p>Build on the foundations.</p></div></li>
            <li><span aria-hidden="true">03</span><div><h4>Advanced</h4><p>Continue into deeper study.</p></div></li>
          </ol>
        </article>
        <article className="courses-offering">
          <div className="courses-offering-heading"><BookOpen size={22} strokeWidth={1.4} aria-hidden="true" /><span>Seven sections · About 75–80 minutes</span></div>
          <h3>Veda Margam — Part 1</h3>
          <p>A seven-section study draft for invited learners, with time to read, reflect and revisit.</p>
          <div className="courses-study-note"><p className="eyebrow">Study at your own pace</p><p>The suggested times are a guide. Leave room for reflection as you move through the course.</p></div>
        </article>
      </div>
    </section>

    <section className="courses-languages container" aria-labelledby="courses-languages-title">
      <div><p className="eyebrow">Languages</p><h2 id="courses-languages-title">Eight language drafts.</h2></div>
      <div><ul aria-label="Draft languages">{courseLanguages.map(language => <li key={language}>{language}</li>)}</ul>
        <p>Courses and translations are drafts awaiting institutional and fluent-language review. Your invitation will confirm the courses and languages available to you.</p>
      </div>
    </section>
    <div className="courses-help container"><p>Questions about studying with the Institute?</p><a className="text-link" href="https://tally.so/r/PddYY0" onClick={onEnquire}>Get in touch <ArrowUpRight size={17} aria-hidden="true" /></a></div>
  </div>;
}
